import React from "react";
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  StatusBar,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AntDesign } from "@expo/vector-icons";
import LottieConfetti from "@/components/LottieConfetti";
import ShareUrlComponent from "@/components/ShareUrlComponent";
import SocialShareBar from "@/components/SocialShareBar";
import { useSocialShare } from "@/hooks/useSocialShare";
import { RefundWithItem } from "@/types/refundWithItem";
import { getCompanyName } from "@/utils/refundableItemTypeGuards";
import useUserStore from "@/store/user";

type Props = {
  visible: boolean;
  onClose: () => void;
  refundWithItem: RefundWithItem;
};

const RefundSuccessModal: React.FC<Props> = ({
  visible,
  onClose,
  refundWithItem,
}) => {
  // Extract display data from refund process and refundable item
  const refundAmount = refundWithItem.refundProcess.approvedRefundAmount || "0";
  const companyName = getCompanyName(refundWithItem.refundableItem);
  const { userProfile } = useUserStore();
  const shareUrl = `https://usesparrow.onelink.me/h5cL/iamwg19q?deep_link_value=${userProfile?.id}`;

  const { handleSocialShare } = useSocialShare({
    refundAmount: String(refundAmount),
    companyName,
    shareUrl,
  });

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <StatusBar barStyle="light-content" backgroundColor="transparent" />
      <LinearGradient
        colors={["#26913B", "#243D3C"]}
        style={styles.gradient}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
      >
        {/* Close button */}
        <TouchableOpacity style={styles.closeButton} onPress={onClose}>
          <AntDesign name="close" size={24} color="#FFFFFF" />
        </TouchableOpacity>

        {/* Confetti animation */}
        <LottieConfetti active={visible} />

        {/* Main content container */}
        <View style={styles.mainContainer}>
          {/* Company icon placeholder */}
          <View style={styles.companyIconContainer}>
            <View style={styles.companyIcon}>
              <Text style={styles.companyIconText}>
                {companyName.charAt(0).toUpperCase()}
              </Text>
            </View>
          </View>

          {/* Success message */}
          <Text style={styles.title}>
            You got a ${refundAmount}
            {"\n"}refund from {companyName}!
          </Text>

          <Text style={styles.subtitle}>
            You&apos;ll see the refund on your statement in the next 3-5 days.
          </Text>

          {/* Share URL */}
          <ShareUrlComponent shareUrl={shareUrl} />

          {/* Social media icons */}
          <SocialShareBar
            onFacebookPress={() => handleSocialShare("facebook")}
            onTwitterPress={() => handleSocialShare("twitter")}
            onInstagramPress={() => handleSocialShare("instagram")}
            onMessengerPress={() => handleSocialShare("messenger")}
          />

          {/* Invitation text */}
          <Text style={styles.inviteText}>
            Invite a friend and you&apos;ll both{"\n"}get an extra $5 refund.
          </Text>
        </View>
      </LinearGradient>
    </Modal>
  );
};

const styles = StyleSheet.create({
  gradient: {
    flex: 1,
    paddingHorizontal: 16,
    justifyContent: "center",
  },
  closeButton: {
    position: "absolute",
    top: 60,
    right: 16,
    zIndex: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  mainContainer: {
    alignItems: "center",
  },
  companyIconContainer: {
    marginBottom: 25,
  },
  companyIcon: {
    width: 104,
    height: 104,
    borderRadius: 52,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderWidth: 2,
    borderColor: "rgba(255, 255, 255, 0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  companyIconText: {
    fontSize: 40,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  title: {
    fontSize: 30,
    fontWeight: "500",
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: 12,
    lineHeight: 36,
    letterSpacing: -0.3,
  },
  subtitle: {
    fontSize: 15,
    color: "#FFFFFF",
    textAlign: "center",
    lineHeight: 23,
    letterSpacing: -0.3,
    marginBottom: 26,
    marginHorizontal: 64,
  },
  inviteText: {
    fontSize: 15,
    color: "#FFFFFF",
    textAlign: "center",
    lineHeight: 23,
    letterSpacing: -0.3,
  },
});

export default RefundSuccessModal;
